import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export interface AdType {
  id: string;
  name: string;
  description: string;
  chatId: string;
  isDefault?: boolean;
  contextNote?: string;
  hasModelSelection?: boolean;
}

interface AdTypeDef {
  id: string;
  chatId: string;
  isDefault?: boolean;
  hasModelSelection?: boolean;
}

const AD_TYPE_DEFS: AdTypeDef[] = [
  { id: "follower-ads", chatId: "chatNode-aged-valley-YQxOP" },
  { id: "vsl-funnel-optin", chatId: "chatNode-gentle-dust-UTU7n", isDefault: true },
  { id: "vsl-funnel-direct", chatId: "chatNode-gentle-dust-UTU7n" },
  { id: "webinar-ads", chatId: "chatNode-lingering-pond-56IZl" },
  { id: "local-business", chatId: "chatNode-holy-dust-8AkOQ" },
  { id: "form-ads", chatId: "chatNode-gentle-dust-UTU7n" },
  { id: "universal-ads", chatId: "chatNode-misty-smoke-RixGV", hasModelSelection: true },
];

export function buildAdTypes(t: (k: string) => string): AdType[] {
  return AD_TYPE_DEFS.map((def) => ({
    id: def.id,
    chatId: def.chatId,
    isDefault: def.isDefault,
    hasModelSelection: def.hasModelSelection,
    name: t(`adTypeSelector.types.${def.id}.name`),
    description: t(`adTypeSelector.types.${def.id}.description`),
    contextNote: t(`adTypeSelector.types.${def.id}.contextNote`),
  }));
}

const AI_MODELS = [
  { id: "claude-4-sonnet-20250514", name: "Claude 4 Sonnet" },
  { id: "o3-mini", name: "O3 Mini" },
  { id: "gpt-4o", name: "GPT-4o" },
  { id: "grok-2-1212", name: "Grok 2" },
  { id: "gpt-4o-mini", name: "GPT-4o Mini" },
];

interface AdTypeSelectorProps {
  selectedAdType: AdType;
  onAdTypeChange: (adType: AdType) => void;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
}

export default function AdTypeSelector({
  selectedAdType,
  onAdTypeChange,
  selectedModel,
  onModelChange,
}: AdTypeSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const adTypes = buildAdTypes(t);
  const resolvedSelected = adTypes.find((a) => a.id === selectedAdType.id) ?? selectedAdType;

  const handleAdTypeSelect = (adType: AdType) => {
    onAdTypeChange(adType);
    // Don't close modal if this ad type requires model selection
    if (!adType.hasModelSelection) {
      setOpen(false);
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg">
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium text-white">{t("adTypeSelector.label")}</span>
        <span className="text-sm text-red-300 font-medium">{resolvedSelected.name}</span>
        {resolvedSelected.hasModelSelection && selectedModel && (
          <>
            <span className="text-white/60">•</span>
            <span className="text-sm text-blue-300 font-medium">
              {AI_MODELS.find((m) => m.id === selectedModel)?.name}
            </span>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="bg-red-600/20 border-red-500 text-red-300 hover:bg-red-600/30"
          >
            {t("adTypeSelector.change")}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{t("adTypeSelector.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("adTypeSelector.dialogDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 overflow-y-auto max-h-[60vh]">
            {adTypes.map((adType) => (
              <div
                key={adType.id}
                className={`p-4 rounded-lg border cursor-pointer transition-all relative ${
                  selectedAdType.id === adType.id
                    ? "border-red-400 bg-red-500 shadow-md"
                    : "border-white/[0.12] bg-[#0a0c10] hover:border-white/[0.15] hover:bg-white/[0.08]"
                }`}
                onClick={() => handleAdTypeSelect(adType)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4
                      className={`font-semibold ${
                        selectedAdType.id === adType.id ? "text-white" : "text-white"
                      }`}
                    >
                      {adType.name}
                    </h4>
                    <p
                      className={`text-sm mt-1 ${
                        selectedAdType.id === adType.id ? "text-red-100" : "text-white/70"
                      }`}
                    >
                      {adType.description}
                    </p>
                    {adType.contextNote && (
                      <p
                        className={`text-xs mt-2 ${
                          selectedAdType.id === adType.id ? "text-red-50" : "text-white/60"
                        }`}
                      >
                        {adType.contextNote}
                      </p>
                    )}
                  </div>
                  {selectedAdType.id === adType.id && (
                    <div className="ml-4 flex-shrink-0">
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-red-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Model Selection for Universal Ads */}
          {resolvedSelected.hasModelSelection && (
            <div className="py-4 border-t border-white/[0.1]">
              <h4 className="font-semibold text-white mb-3">{t("adTypeSelector.modelTitle")}</h4>
              <Select value={selectedModel} onValueChange={onModelChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("adTypeSelector.modelPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {AI_MODELS.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => setOpen(false)}
                className="w-full mt-3 bg-red-600 hover:bg-red-700"
                disabled={!selectedModel}
              >
                {t("adTypeSelector.confirmSelection")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
